import React, { useState, useEffect, useRef } from 'react';
import { KeyRound, ShieldCheck, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { verifyPinRemote } from '../utils/pinGate';
import { toast } from 'sonner';

/**
 * Session-scoped PIN gate modal.
 * Öppnas när admin gör en känslig åtgärd (t.ex. godkänna tidrapport) och PIN
 * inte redan verifierats de senaste 30 minuterna.
 *
 * Props:
 *   open        boolean
 *   onCancel    () => void
 *   onVerified  () => void      — kallas efter lyckad PIN-verifiering
 *   reason      string          — visas i modalen (t.ex. "godkänna 3 tidrapporter")
 */
const PinPromptModal = ({ open, onCancel, onVerified, reason }) => {
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setPin('');
      setBusy(false);
      // Fokusera input efter mount
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!pin || pin.length !== 4) {
      toast.error('Pinkod måste vara 4 siffror');
      return;
    }
    setBusy(true);
    try {
      await verifyPinRemote(pin);
      toast.success('Pinkod verifierad — låst upp i 30 min');
      onVerified?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Felaktig pinkod');
      setPin('');
      inputRef.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center px-4"
      onClick={() => !busy && onCancel?.()}
      data-testid="pin-prompt-modal"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm bg-card border border-border rounded-2xl shadow-2xl overflow-hidden"
      >
        <div className="bg-[#0F0E0D] text-white px-5 py-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#E8613A]/20 flex items-center justify-center">
            <ShieldCheck size={18} className="text-[#E8613A]" />
          </div>
          <div>
            <p className="text-sm font-bold">Bekräfta med pinkod</p>
            <p className="text-[11px] text-white/60">Låser upp känsliga åtgärder i 30 min</p>
          </div>
        </div>
        <div className="p-5 space-y-4">
          {reason && (
            <p className="text-sm text-muted-foreground leading-snug">
              För att {reason}, skriv in din 4-siffriga admin-pinkod.
            </p>
          )}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <KeyRound size={11} /> Pinkod
            </label>
            <Input
              ref={inputRef}
              type="password"
              maxLength={4}
              inputMode="numeric"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="****"
              className="mt-1 h-12 text-center text-xl tracking-[0.5em] font-mono"
              data-testid="pin-prompt-input"
              autoComplete="off"
            />
          </div>
        </div>
        <div className="px-5 pb-5 flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={busy}
            className="flex-1 h-11"
            data-testid="pin-prompt-cancel"
          >
            Avbryt
          </Button>
          <Button
            type="submit"
            disabled={busy || pin.length !== 4}
            className="flex-1 h-11 bg-[#E8613A] hover:bg-[#D4532F] text-white font-bold"
            data-testid="pin-prompt-submit"
          >
            {busy ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Verifierar…</> : 'Bekräfta'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PinPromptModal;
